import React from 'react';
import styled from 'styled-components';
import { ReactComponent as LookerIcon } from '@elementary/common-ui/assets/looker-icon.svg';
import { ReactComponent as AirflowIcon } from '@elementary/common-ui/assets/orchestrators/airflow-icon.svg';
import { ReactComponent as DagsterIcon } from '@elementary/common-ui/assets/orchestrators/dagster-icon.svg';
import { ReactComponent as DbtCloudIcon } from '@elementary/common-ui/assets/orchestrators/dbt-cloud-icon.svg';
import { ReactComponent as GenericOrchestratorIcon } from '@elementary/common-ui/assets/orchestrators/generic-orchestrator-icon.svg';
import { ReactComponent as GithubActionsIcon } from '@elementary/common-ui/assets/orchestrators/github-actions-icon.svg';
import { ReactComponent as PrefectIcon } from '@elementary/common-ui/assets/orchestrators/prefect-icon.svg';
import { ReactComponent as PowerBiIcon } from '@elementary/common-ui/assets/powerbi-icon.svg';
import { ReactComponent as SigmaIcon } from '@elementary/common-ui/assets/sigma-icon.svg';
import { ReactComponent as TableauIcon } from '@elementary/common-ui/assets/tableau-icon.svg';
import { ReactComponent as ThoughtSpotIcon } from '@elementary/common-ui/assets/thoughtspot-icon.svg';
import { ReactComponent as HexIcon } from '@elementary/common-ui/assets/hex-icon.svg';
import { ReactComponent as LightdashIcon } from '@elementary/common-ui/assets/lightdash-icon.svg';
import {
  Flex,
  FlexColumn,
  InfoTooltip,
} from '@elementary/common-ui/components';
import { EMPTY_PLACEHOLDER } from '@elementary/common-ui/constants';
import { capitalize } from '@elementary/common-ui/utils/common';
import { Link, Typography } from '@mui/material';
import { fontSize, radius, space } from '../../style/utils';
import {
  Exposure,
  Invocation,
  OrchestratorType,
  PlatformType,
  Resource,
  ResourceType,
} from './types';
import { capitalizeExposureType } from './utils/capitalizeExposureType';
import { COLLECT_JOB_DATA_DOCS_URL } from './constants';

const InfoSectionWrapper = styled(FlexColumn)`
  flex: 1;
  gap: ${space('sm')};
  min-width: 224px;
`;

const InfoSectionTitle = styled(Typography)`
  font-size: ${fontSize('lg')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  text-transform: capitalize;
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const InfoSectionContent = styled(FlexColumn)`
  height: 100%;
  overflow: auto;
  border-radius: ${radius('lg')};
  padding: ${space('lg')};
  background-color: ${({ theme }) => theme.palette.surface.muted};
`;

const InfoSectionLink = styled(Link)`
  font-size: ${fontSize('base')};
  color: ${({ theme }) => theme.palette.accent.primary};
`;

const InfoSectionSubtitle = styled(Typography)`
  font-size: ${fontSize('sm')};
  font-weight: ${({ theme }) => theme.typography.fontWeightBold};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const metaTooltip =
  "Enhance data governance by adding tags and owners to your resources. Tags and owners can be defined in the config or meta sections of your dbt project's properties YAML files.";
const descriptionTooltip =
  "Add context to your resources by adding a description. Description can be added to your resources in your dbt project's properties YAML files.";
const jobTooltip = (
  <>
    Job information can be captured from your orchestrator using environment
    variables.{' '}
    <Link
      sx={{ fontSize: fontSize('sm') }}
      target="_blank"
      href={COLLECT_JOB_DATA_DOCS_URL}
    >
      Learn more.
    </Link>
  </>
);

const exposureTooltip =
  "Your tables' downstream dependencies, including dashboards, applications, and data science pipelines, are referred to as 'exposures'. These exposures can be defined as YAML files within your dbt project.";

const InfoSection = ({
  title,
  tooltip,
  children,
}: {
  title: string;
  tooltip: React.ReactNode;
  children: React.ReactNode;
}) => (
  <InfoSectionWrapper>
    <Flex sx={{ alignItems: 'center', gap: 1 }}>
      <InfoSectionTitle>{title}</InfoSectionTitle>
      <InfoTooltip title={tooltip} />
    </Flex>
    <InfoSectionContent sx={{ gap: 2 }}>{children}</InfoSectionContent>
  </InfoSectionWrapper>
);

const InfoSectionRow = ({
  subtitle,
  icon,
  text,
  link,
}: {
  subtitle?: string;
  icon?: React.ReactNode;
  text?: string;
  link?: string;
}) => {
  const formattedSubtitle = subtitle ? `${capitalize(subtitle)}:` : null;

  return (
    <FlexColumn sx={{ gap: space('xs') }}>
      <InfoSectionSubtitle>{formattedSubtitle}</InfoSectionSubtitle>
      <Flex sx={{ gap: space('xs'), alignItems: 'center' }}>
        {icon}
        {link ? (
          <InfoSectionLink href={link} target="_blank" rel="noreferrer">
            {text || link}
          </InfoSectionLink>
        ) : (
          <Typography>{text || EMPTY_PLACEHOLDER}</Typography>
        )}
      </Flex>
    </FlexColumn>
  );
};

const orchestratorInfo = {
  [OrchestratorType.Airflow]: {
    name: 'Airflow',
    icon: <AirflowIcon height={24} width={24} />,
  },
  [OrchestratorType.DbtCloud]: {
    name: 'dbt Cloud',
    icon: <DbtCloudIcon height={24} width={24} />,
  },
  [OrchestratorType.GithubActions]: {
    name: 'Github Actions',
    icon: <GithubActionsIcon height={24} width={24} />,
  },
  [OrchestratorType.Dagster]: {
    name: 'Dagster',
    icon: <DagsterIcon height={24} width={24} />,
  },
  [OrchestratorType.Prefect]: {
    name: 'Prefect',
    icon: <PrefectIcon height={24} width={24} />,
  },
  default: {
    name: null,
    icon: <GenericOrchestratorIcon height={24} width={24} />,
  },
};

const getOrchestratorData = (orchestrator?: string) => {
  const formattedOrchestratorName = orchestrator?.toLowerCase();

  if (formattedOrchestratorName == null) return null;

  return (
    orchestratorInfo[formattedOrchestratorName as OrchestratorType] || {
      ...orchestratorInfo.default,
      name: formattedOrchestratorName,
    }
  );
};

const getExposureUrlPlaceholder = (platform?: string) =>
  `View in ${capitalize(platform)}`;

const getPlatformIcon = (platform?: string) => {
  switch (platform?.toLowerCase()) {
    case PlatformType.Tableau:
      return <TableauIcon height={24} width={24} />;
    case PlatformType.Looker:
      return <LookerIcon height={24} width={24} />;
    case PlatformType.PowerBI:
      return <PowerBiIcon height={24} width={24} />;
    case PlatformType.Sigma:
      return <SigmaIcon height={24} width={24} />;
    case PlatformType.ThoughtSpot:
      return <ThoughtSpotIcon height={24} width={24} />;
    case 'hex':
      return <HexIcon height={24} width={24} />;
    case PlatformType.Lightdash:
      return <LightdashIcon height={24} width={24} />;
    default:
      return undefined;
  }
};

type Props = {
  node: Resource;
  invocation: Invocation;
};

const JobInfoSection = ({ invocation }: { invocation: Invocation }) => {
  const { orchestrator, jobId, jobName, jobUrl, jobRunUrl, jobRunId } =
    invocation;
  const orchestratorData = getOrchestratorData(orchestrator);

  return (
    <InfoSection title="job" tooltip={jobTooltip}>
      <InfoSectionRow
        subtitle="orchestrator"
        icon={orchestratorData?.icon}
        text={orchestratorData?.name}
      />
      <InfoSectionRow subtitle="job id" text={jobId} />
      <InfoSectionRow subtitle="run id" text={jobRunId} />
      <InfoSectionRow subtitle="job name" text={jobName} />
      {jobUrl ? <InfoSectionRow subtitle="job url" link={jobUrl} /> : null}
      {jobRunUrl ? (
        <InfoSectionRow subtitle="run url" link={jobRunUrl} />
      ) : null}
    </InfoSection>
  );
};

const ExposureInfoSection = ({ node }: { node: Resource }) => {
  if (node.resourceType !== ResourceType.Exposure) {
    return null;
  }
  const { type, url, meta } = node as Exposure;
  const platform = meta?.platform;
  const urlPlaceholder = getExposureUrlPlaceholder(platform);

  return (
    <InfoSection title="exposure" tooltip={exposureTooltip}>
      <InfoSectionRow
        subtitle="type"
        text={type ? capitalizeExposureType(type) : type}
      />
      {url ? (
        <InfoSectionRow
          subtitle="url"
          text={urlPlaceholder}
          link={url}
          icon={getPlatformIcon(platform)}
        />
      ) : (
        <InfoSectionRow
          subtitle="url"
          text={platform}
          icon={getPlatformIcon(platform)}
        />
      )}
    </InfoSection>
  );
};

const NodeInfo = ({ node, invocation }: Props) => {
  const { description, owners, tags, resourceType } = node || {};

  return (
    <Flex
      gap={1}
      sx={{
        paddingBottom: space('huge'),
        height: '100%',
      }}
    >
      <InfoSection title="meta" tooltip={metaTooltip}>
        <InfoSectionRow subtitle="owners" text={owners?.join(', ')} />
        <InfoSectionRow subtitle="tags" text={tags?.join(', ')} />
      </InfoSection>

      {resourceType === ResourceType.Exposure ? (
        <ExposureInfoSection node={node} />
      ) : (
        <JobInfoSection invocation={invocation} />
      )}

      <InfoSection title="description" tooltip={descriptionTooltip}>
        <InfoSectionRow text={description} />
      </InfoSection>
    </Flex>
  );
};

export default NodeInfo;
